import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  useTheme,
  alpha,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Computer as ComputerIcon,
  Description as DescriptionIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
  AccountBalance as AccountBalanceIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { formateador } from '../../helper/Formateador';

const EjemploCredito = () => {
  const theme = useTheme();

  // Valores del ejemplo proporcionados
  const montoSolicitado = 300000;
  const plazoDias = 90;
  const tasaSuperfinanciera = '24,36% E.A.';
  const mesSuperfinanciera = 'enero 2026';
  const interes = 5630;
  const plataformaServicios = 72000;
  const estudioFirmaDigital = 25500;
  const reactivacionPlataforma = 158000;
  const iva = 18525;
  const totalPagar = 579655;

  // Configuración de conceptos con iconos y colores
  const conceptos = [
    {
      id: 'interes',
      titulo: 'Interés (24% E.A.)',
      valor: interes,
      icono: TrendingUpIcon,
      color: '#2196F3',
      gradiente: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
    },
    {
      id: 'plataforma',
      titulo: 'Plataforma de Servicios (Opcional)',
      valor: plataformaServicios,
      icono: ComputerIcon,
      color: '#4CAF50',
      gradiente: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
    },
    {
      id: 'estudio',
      titulo: 'Estudio y Firma Digital (Opcional)',
      valor: estudioFirmaDigital,
      icono: DescriptionIcon,
      color: '#FF9800',
      gradiente: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
    },
    {
      id: 'reactivacion',
      titulo: 'Reactivación de Plataforma',
      valor: reactivacionPlataforma,
      icono: RefreshIcon,
      color: '#9C27B0',
      gradiente: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)',
    },
    {
      id: 'iva',
      titulo: 'IVA (19%)',
      valor: iva,
      icono: ReceiptIcon,
      color: '#F44336',
      gradiente: 'linear-gradient(135deg, #F44336 0%, #D32F2F 100%)',
    },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        marginTop: { xs: '40px', sm: '60px' },
        marginBottom: { xs: '40px', sm: '60px' },
        padding: { xs: '0 16px', sm: '0 24px' },
        position: 'relative',
      }}
    >
      <Card
        sx={{
          maxWidth: { xs: '100%', sm: '800px', md: '900px' },
          width: '100%',
          boxShadow: '0px 8px 32px rgba(0, 116, 188, 0.2), 0px 4px 16px rgba(0, 0, 0, 0.1)',
          borderRadius: '24px',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 50%, #f5f7fa 100%)',
          border: '2px solid rgba(0, 116, 188, 0.08)',
          position: 'relative',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at top right, rgba(255, 222, 46, 0.03) 0%, transparent 50%)',
            pointerEvents: 'none',
          },
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0px 12px 40px rgba(0, 116, 188, 0.3), 0px 6px 20px rgba(0, 0, 0, 0.15)',
            borderColor: 'rgba(0, 116, 188, 0.15)',
          },
        }}
      >
        <CardContent
          sx={{
            padding: { xs: '20px', sm: '24px', md: '28px' },
            background: 'transparent',
          }}
        >
          {/* Título Principal */}
          <Box
            sx={{
              textAlign: 'center',
              marginBottom: '20px',
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: '-8px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '80px',
                height: '4px',
                background: 'linear-gradient(90deg, #0074bc 0%, #FFDE2E 100%)',
                borderRadius: '2px',
              },
            }}
          >
            <Typography
              variant="h4"
              component="h2"
              sx={{
                fontWeight: 800,
                background: 'linear-gradient(135deg, #0074bc 0%, #005a94 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                textAlign: 'center',
                fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                letterSpacing: '-0.5px',
              }}
            >
              Ejemplo de tu Crédito a {plazoDias} días
            </Typography>
          </Box>

          {/* Tasa Superfinanciera - Muy Importante */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #0074bc 0%, #005a94 100%)',
              borderRadius: '16px',
              padding: { xs: '16px', sm: '18px' },
              marginBottom: '20px',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0px 8px 24px rgba(0, 116, 188, 0.3)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '-50%',
                right: '-50%',
                width: '200%',
                height: '200%',
                background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
                animation: 'pulse 3s ease-in-out infinite',
              },
              '@keyframes pulse': {
                '0%, 100%': { opacity: 0.3 },
                '50%': { opacity: 0.6 },
              },
            }}
          >
            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                marginBottom: '6px',
              }}
            >
              <AccountBalanceIcon
                sx={{
                  fontSize: { xs: '22px', sm: '24px' },
                  color: '#FFDE2E',
                  filter: 'drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.2))',
                }}
              />
              <Typography
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  color: '#ffffff',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Tasa usura Superfinanciera {mesSuperfinanciera}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                color: '#FFDE2E',
                fontWeight: 800,
                textShadow: '0px 2px 8px rgba(0, 0, 0, 0.3)',
                letterSpacing: '1px',
              }}
            >
              {tasaSuperfinanciera}
            </Typography>
          </Box>

          {/* Información del Crédito */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #f0f7ff 0%, #e8f4ff 100%)',
              borderRadius: '12px',
              padding: { xs: '14px', sm: '16px' },
              marginBottom: '20px',
              border: '1px solid rgba(0, 116, 188, 0.15)',
              boxShadow: '0px 4px 12px rgba(0, 116, 188, 0.1)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <InfoIcon sx={{ color: '#0074bc', fontSize: '20px', marginTop: '2px', flexShrink: 0 }} />
              <Typography
                sx={{
                  fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                  color: '#333',
                  lineHeight: 1.6,
                  textAlign: 'justify',
                  flex: 1,
                }}
              >
                Para un <strong style={{ color: '#0074bc' }}>Monto Solicitado de ${formateador(montoSolicitado)}</strong> a un{' '}
                <strong style={{ color: '#0074bc' }}>plazo de {plazoDias} Días</strong>, la liquidación se compone de:
              </Typography>
            </Box>
          </Box>

          {/* Desglose de Costos */}
          <Box sx={{ marginBottom: '20px' }}>
            <Grid container spacing={2}>
              {conceptos.map((concepto) => {
                const IconComponent = concepto.icono;
                return (
                  <Grid item xs={12} sm={6} md={4} key={concepto.id}>
                    <Box
                      sx={{
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
                        border: `2px solid ${alpha(concepto.color, 0.2)}`,
                        borderRadius: '12px',
                        padding: { xs: '14px', sm: '16px' },
                        height: '100%',
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        cursor: 'pointer',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '3px',
                          background: concepto.gradiente,
                          transform: 'scaleX(0)',
                          transformOrigin: 'left',
                          transition: 'transform 0.3s ease',
                        },
                        '&:hover': {
                          transform: 'translateY(-6px)',
                          boxShadow: `0px 8px 24px ${alpha(concepto.color, 0.3)}, 0px 4px 12px ${alpha(concepto.color, 0.2)}`,
                          borderColor: alpha(concepto.color, 0.4),
                          '&::before': {
                            transform: 'scaleX(1)',
                          },
                          '& .icon-container': {
                            transform: 'scale(1.1) rotate(5deg)',
                            background: concepto.gradiente,
                          },
                        },
                      }}
                    >
                      <Box
                        className="icon-container"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: { xs: '40px', sm: '44px' },
                          height: { xs: '40px', sm: '44px' },
                          borderRadius: '10px',
                          background: alpha(concepto.color, 0.1),
                          marginBottom: '10px',
                          transition: 'all 0.3s ease',
                        }}
                      >
                        <IconComponent
                          sx={{
                            fontSize: { xs: '22px', sm: '24px' },
                            color: concepto.color,
                            transition: 'color 0.3s ease',
                          }}
                        />
                      </Box>
                      <Typography
                        sx={{
                          fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                          color: '#555',
                          marginBottom: '8px',
                          fontWeight: 600,
                          lineHeight: 1.3,
                        }}
                      >
                        {concepto.titulo}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.375rem' },
                          background: concepto.gradiente,
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          fontWeight: 800,
                          letterSpacing: '-0.5px',
                        }}
                      >
                        ${formateador(concepto.valor)}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>

          {/* Divider Decorativo */}
          <Box
            sx={{
              marginY: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <Box
              sx={{
                flex: 1,
                height: '2px',
                background: 'linear-gradient(90deg, transparent 0%, #0074bc 50%, transparent 100%)',
              }}
            />
            <Box
              sx={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #0074bc 0%, #FFDE2E 100%)',
                boxShadow: '0px 0px 12px rgba(0, 116, 188, 0.5)',
              }}
            />
            <Box
              sx={{
                flex: 1,
                height: '2px',
                background: 'linear-gradient(90deg, transparent 0%, #0074bc 50%, transparent 100%)',
              }}
            />
          </Box>

          {/* Total a Pagar */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #0074bc 0%, #005a94 100%)',
              borderRadius: '16px',
              padding: { xs: '20px', sm: '24px', md: '28px' },
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0px 12px 40px rgba(0, 116, 188, 0.4), 0px 4px 16px rgba(0, 0, 0, 0.2)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '-50%',
                left: '-50%',
                width: '200%',
                height: '200%',
                background: 'radial-gradient(circle, rgba(255, 222, 46, 0.15) 0%, transparent 70%)',
                animation: 'rotate 20s linear infinite',
              },
              '@keyframes rotate': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography
                sx={{
                  fontSize: { xs: '0.8125rem', sm: '0.9375rem' },
                  color: '#ffffff',
                  marginBottom: '8px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '2px',
                  opacity: 0.95,
                }}
              >
                TOTAL A PAGAR
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '2rem', sm: '2.75rem', md: '3.25rem' },
                  background: 'linear-gradient(135deg, #FFDE2E 0%, #FFC107 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  fontWeight: 900,
                  textShadow: '0px 4px 12px rgba(0, 0, 0, 0.3)',
                  letterSpacing: '-2px',
                  lineHeight: 1.1,
                }}
              >
                ${formateador(totalPagar)}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EjemploCredito;
